import { FC, Ref, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { Button, Form, Input } from 'antd';

import { AppDispatch } from 'app/store';

import ReCaptcha, { type ReCaptchaRef } from 'components/ReCaptcha/ReCaptcha';
import useCaptchaSubmit, { GetFreshTokenFn } from 'components/ReCaptcha/hooks/useCaptchaSubmit';

import { setPasswordLength } from 'features/auth/authSlice';

import { MINIMAL_PASSWORD_LENGTH } from 'constants/uiSettings';

import { createPasswordValidationRule } from 'utils/validators';

import { ReactComponent as EmailIcon } from 'assets/icons/inputEmail.svg';

import styles from './SetPasswordForm.module.scss';

export interface SetPasswordFormValues {
  email: string;
  password: string;
  passwordConfirmation: string;
}

interface SetPasswordFormProps {
  email: string;
  onSubmit: (
    values: SetPasswordFormValues,
    getFreshToken: GetFreshTokenFn
  ) => void | Promise<void>;
  isLoading: boolean;
  onCancel?: () => void;
}

const SetPasswordForm: FC<SetPasswordFormProps> = ({
  email,
  onSubmit: submitWithRecaptcha,
  isLoading,
  onCancel
}: SetPasswordFormProps) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const pendingValuesRef = useRef<SetPasswordFormValues | null>(null);

  const [form] = Form.useForm<SetPasswordFormValues>();

  const {
    recaptchaRef,
    handleCaptchaChange,
    handleCaptchaError,
    handleCaptchaExpired,
    handleFormSubmit
  } = useCaptchaSubmit({
    onMultiStepSubmit: async (getFreshToken) => {
      const values = pendingValuesRef.current;
      if (!values) {
        return;
      }
      await submitWithRecaptcha(values, getFreshToken);
    }
  });

  const onFinish = (values: SetPasswordFormValues) => {
    pendingValuesRef.current = values;
    void handleFormSubmit();
  };

  return (
    <div className="login-container">
      <h2 className="title">{t('page.auth.setPassword.title')}</h2>
      <Form
        form={form}
        name={t('form.createPassword')}
        initialValues={{ email }}
        onFinish={onFinish}
        autoComplete="off"
        className={styles.setPasswordForm}
      >
        <Form.Item name="email" label={t('form.email')} className={styles.item}>
          <Input
            type="email"
            placeholder={t('form.email.placeholder')}
            suffix={<EmailIcon />}
            disabled
            className={styles.input}
          />
        </Form.Item>
        <Form.Item
          name="password"
          label={t('form.password.newPassword', {
            amount: MINIMAL_PASSWORD_LENGTH
          })}
          rules={[createPasswordValidationRule(t)]}
          className={styles.item}
        >
          <Input.Password
            placeholder={t('form.password.pleaseEnter')}
            onChange={(e) => dispatch(setPasswordLength(e.target.value.length))}
          />
        </Form.Item>
        <Form.Item
          name="passwordConfirmation"
          label={t('form.passwordConfirm')}
          dependencies={['password']}
          rules={[
            {
              required: true,
              message: t('form.passwordConfirm.validation.confirm')
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(t('form.passwordCopy.validation.noMatch'))
                );
              }
            })
          ]}
          className={styles.item}
        >
          <Input.Password placeholder={t('form.passwordCopy.placeholder')} />
        </Form.Item>
        <div className={styles.buttonsContainer}>
          {onCancel && (
            <Button onClick={onCancel}>
              {t('page.auth.setPassword.backToLogin')}
            </Button>
          )}
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {t('page.auth.setPassword.setMyPassword')}
          </Button>
        </div>

        <ReCaptcha
          ref={recaptchaRef as Ref<ReCaptchaRef>}
          onChange={handleCaptchaChange}
          onError={handleCaptchaError}
          onExpired={handleCaptchaExpired}
        />
      </Form>
    </div>
  );
};

export default SetPasswordForm;
